import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { buildNavItems, getNavSearchItems } from '@/config/navTree';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { isAdmin, normalizeRole } from '@/utils/roles';

type NavSearchCommandProps = {
  className?: string;
};

export function NavSearchCommand({ className }: NavSearchCommandProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { user } = useAuth();
  const userIsAdmin = isAdmin(normalizeRole(user?.role));
  const [search, setSearch] = useState('');

  const { items, rootItemId } = useMemo(() => buildNavItems(userIsAdmin), [userIsAdmin]);
  const searchItems = useMemo(() => getNavSearchItems(items, rootItemId), [items, rootItemId]);
  const searchGroups = useMemo(() => {
    const groups = new Map<string, typeof searchItems>();
    for (const item of searchItems) {
      const group = groups.get(item.group) ?? [];
      group.push(item);
      groups.set(item.group, group);
    }
    return groups;
  }, [searchItems]);
  const showSearchResults = search.trim().length > 0;

  return (
    <div className={cn('relative z-40', className)}>
      <Command className="overflow-visible bg-transparent p-0 **:data-[selected=true]:bg-muted **:data-selected:bg-transparent">
        <CommandInput placeholder="Search navigation..." value={search} onValueChange={setSearch} />
        {showSearchResults && (
          <CommandList className="absolute top-full right-0 left-0 z-50 mt-1 max-h-48 rounded-xl border border-border-default bg-white p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10">
            <CommandEmpty>No results found.</CommandEmpty>
            {[...searchGroups.entries()].map(([group, groupItems]) => (
              <CommandGroup key={group} heading={group}>
                {groupItems.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.name} ${item.group}`}
                    className={cn(pathname === item.path && 'font-semibold')}
                    onSelect={() => {
                      navigate({ to: item.path });
                      setSearch('');
                    }}
                  >
                    <span>{item.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        )}
      </Command>
    </div>
  );
}
